<script setup lang="ts">
import type { ExtendedLocationDetail } from '@/types/location';
import type { ErrorCollection } from '@/utils/validator';
import type { TranslationFormItem } from '@/types/translation';

import dayjs from 'dayjs';

const mapOptions = {
	fields: ['geometry', 'name', 'formatted_address'],
	strictBounds: false,
	types: ['(cities)'],
	componentRestrictions: { country: 'jp' },
};

const props = defineProps({
	data: {
		type: Object as PropType<TranslationFormItem>,
		default: () => {},
		required: true,
	},
	destinationIndex: {
		type: Number,
		default: null,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	errors: {
		type: Array as PropType<Array<ErrorCollection>>,
		required: true,
		default: () => [],
	},
});

const emit = defineEmits<{
	(e: 'onChange', data: TranslationFormItem): void;
	(e: 'onRemove'): void;
	(e: 'onPressEnter'): void;
}>();

const { isMobile } = useWindowResize();

const formData = reactive<TranslationFormItem>({
	arrive: '',
	depart: '',
	location: '',
	experiences: {
		tradition: false,
		food: false,
		nature: false,
		anime: false,
		workshop: false,
	},
	coordinates: null,
	countryCode: '',
});

const timezone = getTimezone();
const defaultArrivalDate = dayjs()
	.tz(timezone)
	.add(7, 'day')
	.startOf('day')
	.utc(true)
	.format(DATE_TIME_ISO8601_FORMAT);
const defaultDepartureDate = dayjs()
	.tz(timezone)
	.add(14, 'day')
	.startOf('day')
	.utc(true)
	.format(DATE_TIME_ISO8601_FORMAT);

const dateRange = ref<string[]>([defaultArrivalDate, defaultDepartureDate]);

const userTimezone = computed(() => timezone);
const suggestedDateRange = computed(() => {
	const today = dayjs().tz(userTimezone.value).startOf('day');
	return {
		start: today.add(7, 'day'),
		end: today.add(14, 'day'),
	};
});
const suggestedDatePlaceholder = computed(() => {
	return `${suggestedDateRange.value.start.format('MMM D')} - ${suggestedDateRange.value.end.format('MMM D')}`;
});
const dateErrorMessage = computed(() => {
	const arriveErrorMessage = getValidationErrorMessageCollection(
		props.errors,
		props.destinationIndex,
		'arrive',
	);
	if (arriveErrorMessage) {
		return arriveErrorMessage;
	}

	const departErrorMessage = getValidationErrorMessageCollection(
		props.errors,
		props.destinationIndex,
		'depart',
	);

	if (departErrorMessage) {
		return departErrorMessage;
	}

	return '';
});

const arriveDateErrorObj = computed(() => props.errors?.[props.destinationIndex]?.arrive);
const departDateErrorObj = computed(() => props.errors?.[props.destinationIndex]?.depart);
const dateRangeErrorObj = computed(() => {
	const errors = [];
	if (arriveDateErrorObj.value) {
		errors.push(...arriveDateErrorObj.value);
	}
	if (departDateErrorObj.value) {
		errors.push(...departDateErrorObj.value);
	}
	return errors;
});
const locationErrorMessage = computed(() => getValidationErrorMessageCollection(props.errors, props.destinationIndex, 'location'));

function handleDateRangeChange(date: string | string[]) {
	formData.arrive = date?.[0];
	formData.depart = date?.[1];

	if (typeof date === 'object' && date) {
		dateRange.value = date;
	}
}

function handleLocationSelected(location: ExtendedLocationDetail) {
	formData.location = location.address || location.name;
	formData.coordinates = location.coordinate;
	formData.countryCode = location.countryCode || '';
}

// Watch for changes in the props.formData and update formData
watch(
	() => props.data,
	(newData) => {
		formData.arrive = newData.arrive;
		formData.depart = newData.depart;
		formData.location = newData.location;
		formData.experiences = newData.experiences;
		formData.coordinates = newData.coordinates;
		formData.countryCode = newData.countryCode;

		if (newData.arrive && newData.depart) {
			dateRange.value = [newData.arrive, newData.depart];
		} else {
			dateRange.value = [];
		}
	},
);

watch(
	() => formData,
	() => {
		emit('onChange', formData);
	},
	{ deep: true },
);

// Assign props.data to formData initially
onMounted(() => {
	if (props.data) {
		formData.arrive = props.data.arrive;
		formData.depart = props.data.depart;
		formData.location = props.data.location;
		formData.experiences = props.data.experiences;
		formData.coordinates = props.data.coordinates;
		formData.countryCode = props.data.countryCode;
	}

	if (formData.arrive && formData.depart) {
		dateRange.value = [formData.arrive, formData.depart];
	}
});
</script>

<template>
	<div class="destination-container">
		<div class="destination-input">
			<div class="form-items">
				<BaseGoogleAutoComplete
					id="location"
					v-model="formData.location"
					placeholder="Tokyo, Japan"
					:error-message="locationErrorMessage"
					:options="mapOptions"
					class="visiting-japan-input"
					@on-select-location="handleLocationSelected"
					@enter="emit('onPressEnter')"
				/>
			</div>

			<div class="form-items">
				<div class="date-group-container">
					<BaseDateTimePicker
						id="daterange"
						v-model="dateRange"
						:date="dateRange"
						:timezone="userTimezone"
						:date-errors="dateRangeErrorObj"
						icon-color="#EE4148"
						is-teleport-date-picker
						:is-centered-date-picker="isMobile"
						:date-placeholder="suggestedDatePlaceholder"
						display-format="MMM D"
						is-multi-calendar-range
						is-date-only
						class="visiting-japan-input"
						@on-date-change="handleDateRangeChange"
					/>
				</div>
				<p
					v-if="dateErrorMessage"
					class="error-message"
				>
					{{ dateErrorMessage }}
				</p>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.destination-container {
	display: flex;
	flex-direction: column;

	.destination-input {
		display: flex;
		flex-direction: column;
		gap: spacings-get(3);

		:deep(.error-message) {
			color: colors-get(base, white);
		}

		:deep(.text-field) {
			border-radius: rem(7);
		}
	}

	.form-items {
		display: flex;
		flex-direction: column;
		width: 100%;
		@include fonts-get(medium, text-sm);
	}

	.date-group-container {
		display: flex;

		:deep(.date-time-group) {
			width: 100%;
		}

		:deep(.dp__menu) {
			z-index: 9999 !important;
		}
	}

	.error-message {
		color: colors-get(base, white);
		@include fonts-get(regular, text-sm);
		margin-top: spacings-get(1);
	}
}
</style>
